import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Card, Row, Col, Table, Accordion, Badge } from 'react-bootstrap';
import { JSONLD, Generic, GenericCollection } from 'react-structured-data';

import Icon from '../../../../components/Content/Icon.js';
import ProductWeight from './ProductWeight.js';
import { getTranslation } from '../../../Intl/IntlHelper.js';
import { dateFormat } from '../../../../util/date.js';

// -----------------------------------------------------------------------------
// PROPOSITION - données inventées pour le moment.
// A remplacer par de vraies données produit (product.*, attributs, ou blocs CMS
// traduits par langue). Tout est regroupé ici pour faciliter le câblage futur.
// -----------------------------------------------------------------------------
function getFactSheet(product, locale) {
    return {
        // Origine / traçabilité (E-E-A-T + confiance)
        origin: 'Catalogne, Espagne',
        producer: product.brand || 'Keesbo',
        madeIn: 'Fabriqué en Espagne',
        label: 'Recette artisanale traditionnelle catalane',

        // Ingrédients (liste dédiée, plus lisible qu'un paragraphe)
        ingredients: [
            'Viande de porc (92%)',
            'Sel',
            'Poivre',
            'Ferments naturels',
            'Boyau naturel comestible',
            'Conservateur naturel : ferments lactiques',
        ],

        // Allergènes mis en évidence (obligation d'information denrées)
        allergens: ['Lait (traces éventuelles)'],
        noAllergens: 'Sans gluten - Sans arachide',

        // Valeurs nutritionnelles pour 100 g
        nutrition: [
            { label: 'Énergie', value: '1 490 kJ / 358 kcal' },
            { label: 'Matières grasses', value: '28 g', sub: 'dont acides gras saturés' },
            { label: 'Glucides', value: '1,2 g', sub: 'dont sucres' },
            { label: 'Protéines', value: '25 g' },
            { label: 'Sel', value: '3,8 g' },
        ],

        // Conservation spécifique au produit
        conservation: 'À conserver dans un endroit frais et sec (12-16 °C). Après ouverture, conserver au réfrigérateur et consommer sous 15 jours. Se déguste à température ambiante.',

        // Signature E-E-A-T (expérience / expertise)
        author: 'Sélectionné et dégusté par l\'équipe Directos',
        datePublished: '2025-01-15',
        dateModified: '2026-06-20',

        // Q&A propre AU produit (citabilité IA - passages autoportants)
        faqs: [
            {
                q: 'Comment se mange le fuet catalan ?',
                a: `Le fuet catalan "${getTranslation(product, 'name', locale) || 'Espetec'}" se déguste à température ambiante, coupé en fines tranches. Il accompagne l'apéritif, se marie avec un vin rouge léger, du fromage ou du pain de campagne. Retirez le boyau si vous le préférez, il est comestible mais facultatif.`,
            },
            {
                q: 'Combien de temps se conserve un fuet catalan ?',
                a: 'Non ouvert, le fuet catalan se conserve plusieurs semaines dans un endroit frais et sec (12 à 16 °C). Une fois entamé, conservez-le au réfrigérateur et consommez-le sous 15 jours. La fleur blanche en surface est naturelle et signe une maturation réussie.',
            },
            {
                q: 'Le fuet catalan contient-il du porc ?',
                a: 'Oui. Le fuet catalan est un saucisson sec composé à plus de 90 % de viande de porc, assaisonnée de sel et de poivre, puis séchée dans un boyau naturel. Il ne convient donc pas aux régimes sans porc.',
            },
            {
                q: 'Quelle est la différence entre le fuet et le chorizo ?',
                a: 'Le fuet est un saucisson sec fin, doux et poivré, sans paprika. Le chorizo, lui, tire sa couleur rouge et son goût piquant du paprika (pimentón). Le fuet a donc un goût plus neutre et une texture plus fine que le chorizo.',
            },
        ],
    };
}

function ProductFactSheet({ intl, product }) {
    const sheet = getFactSheet(product, intl.locale);

    return (
        <Fragment>
            {/* Signature E-E-A-T : auteur + fraîcheur du contenu */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3 small text-black-50">
                <span><Icon icon="check" className="text-secondary me-1" />{sheet.author}</span>
                <span className="d-none d-sm-inline">·</span>
                <span>Publié le {dateFormat(sheet.datePublished, 'DD/MM/YYYY')}</span>
                <span className="d-none d-sm-inline">·</span>
                <span>Mis à jour le {dateFormat(sheet.dateModified, 'DD/MM/YYYY')}</span>
            </div>

            {/* Fiche produit structurée - card blanche, ombre ambient, en-tête séparé */}
            <Card className="mb-4 border-0 shadow">
                <Card.Header>
                    <span className="h5 mb-0 d-inline-flex align-items-center">
                        <Icon icon="info" className="text-secondary me-2" />Fiche produit
                    </span>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col xs="12" md="6" className="mb-3 mb-md-0">
                            <h3 className="h6 fw-semibold mb-2">Ingrédients</h3>
                            <ul className="ps-3 mb-3">
                                {sheet.ingredients.map(ingredient => <li key={ingredient}>{ingredient}</li>)}
                            </ul>

                            <h3 className="h6 fw-semibold mb-2">Allergènes</h3>
                            <p className="mb-1">
                                {sheet.allergens.map(allergen => (
                                    <Badge key={allergen} bg="warning" text="dark" className="me-1 mb-1">
                                        <Icon icon="danger" className="me-1" />{allergen}
                                    </Badge>
                                ))}
                            </p>
                            <p className="small text-success mb-0"><Icon icon="check" className="me-1" />{sheet.noAllergens}</p>
                        </Col>

                        <Col xs="12" md="6">
                            <h3 className="h6 fw-semibold mb-2">Caractéristiques</h3>
                            <Table borderless size="sm" className="mb-0">
                                <tbody>
                                    <tr>
                                        <th className="fw-normal text-black-50">Origine</th>
                                        <td className="text-end"><strong>{sheet.origin}</strong></td>
                                    </tr>
                                    <tr>
                                        <th className="fw-normal text-black-50">Producteur</th>
                                        <td className="text-end"><strong>{sheet.producer}</strong></td>
                                    </tr>
                                    {product.weight > 0 && (
                                        <tr>
                                            <th className="fw-normal text-black-50">Poids</th>
                                            <td className="text-end"><strong><ProductWeight product={product} hideLabel /></strong></td>
                                        </tr>
                                    )}
                                    <tr>
                                        <th className="fw-normal text-black-50">Fabrication</th>
                                        <td className="text-end"><strong>{sheet.madeIn}</strong></td>
                                    </tr>
                                    <tr>
                                        <th className="fw-normal text-black-50">Recette</th>
                                        <td className="text-end"><strong>{sheet.label}</strong></td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Col>
                    </Row>

                    <hr className="text-black-50" />

                    <Row>
                        <Col xs="12" md="6" className="mb-3 mb-md-0">
                            <h3 className="h6 fw-semibold mb-2">Valeurs nutritionnelles <small className="fw-normal text-black-50">(pour 100 g)</small></h3>
                            <Table size="sm" className="mb-0">
                                <tbody>
                                    {sheet.nutrition.map(row => (
                                        <tr key={row.label}>
                                            <th className="fw-normal">{row.label}{row.sub && <small className="d-block text-black-50">{row.sub}</small>}</th>
                                            <td className="text-end align-middle"><strong>{row.value}</strong></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Col>
                        <Col xs="12" md="6">
                            <h3 className="h6 fw-semibold mb-2">Conservation</h3>
                            <p className="mb-0">{sheet.conservation}</p>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Q&A propre au produit - section sur fond Lin Crème (bg-info) */}
            <div className="bg-info rounded p-3 p-md-4 mb-4">
                <h2 className="h4 mb-3">Questions fréquentes sur le fuet catalan</h2>
                <Accordion>
                    {sheet.faqs.map((faq, index) => (
                        <Accordion.Item eventKey={`${index}`} key={faq.q}>
                            <Accordion.Header>
                                <h3 className="h6 fw-semibold mb-0">{faq.q}</h3>
                            </Accordion.Header>
                            <Accordion.Body>{faq.a}</Accordion.Body>
                        </Accordion.Item>
                    ))}
                </Accordion>
            </div>

            {/* JSON-LD FAQPage : conservé comme signal de citation IA (rich result FAQ retiré) */}
            <JSONLD dangerouslyExposeHtml>
                <Generic jsonldtype="FAQPage">
                    <GenericCollection type="mainEntity">
                        {sheet.faqs.map(faq => (
                            <Generic key={faq.q} jsonldtype="Question" schema={{ name: faq.q }}>
                                <Generic type="acceptedAnswer" jsonldtype="Answer" schema={{ text: faq.a }} />
                            </Generic>
                        ))}
                    </GenericCollection>
                </Generic>
            </JSONLD>
        </Fragment>
    );
}

ProductFactSheet.propTypes = {
    intl: PropTypes.object.isRequired,
    product: PropTypes.object.isRequired,
};

export default injectIntl(ProductFactSheet);
